/**
 * shawq-gtm-events.js
 * ════════════════════════════════════════════════════════════════
 * SINGLE consolidated storefront script.
 * REPLACES: gtm_datalayer.liquid AND gtmdata.js
 *
 * Upload as a theme asset, then load from shawq-gtm-head.liquid:
 *   {{ 'shawq-gtm-events.js' | asset_url | script_tag }}
 *
 * What it does:
 *   - add_to_cart_stape / remove_from_cart_stape / begin_checkout_stape to dataLayer
 *   - fbc / fbp / utm persistence (cookies + localStorage)
 *   - Attribution sync to cart attributes (chunked for long fbc)
 *   - Checkout-gate to ensure sync before navigation
 *   - Guest user data capture (email/phone from any input) via cookie + cart attributes
 *   - Shop Pay Buy Now guard: syncs attribution and checkout intent before redirect
 *   - Dynamic checkout / iframe attribution detection
 *   - Pixel bridge (postMessage from custom pixel sandbox)
 *
 *   v08 (2026-06-19) — Identity + fbc continuity spine:
 *   - IndexedDB recovery of external_id AND fbc/fbclid (not just PII), so a
 *     paid Meta visitor who returns later via "link in bio" keeps the SAME
 *     external_id and original fbc -> the purchase still credits the ad via CAPI.
 *   - First-touch paid identifiers (fbc/fbclid/campaign_id/adset_id/ad_id) are
 *     promoted to top-level on Stape events when the current URL is unpaid,
 *     with paid_click / attributed_channel flags. Current-URL values always
 *     win; first-touch only fills gaps.
 * ════════════════════════════════════════════════════════════════
 */
(function () {
    'use strict';

    var dataLayer = window.dataLayer = window.dataLayer || [];
    var gtm = window.shawqGtm || {};

    var CHECKOUT_BUTTON_SELECTOR =
        'button[name="checkout"], input[name="checkout"], ' +
        '.shopify-payment-button__button, [data-shopify="payment-button"]';
    var DYNAMIC_CHECKOUT_CONTAINER_SELECTOR = '.shopify-payment-button';
    var DYNAMIC_CHECKOUT_SELECTOR =
        '.shopify-payment-button__button, .shopify-payment-button iframe';

    var BEGIN_CHECKOUT_DEDUP_WINDOW_MS = 30000;
    var CHECKOUT_INTENT_WINDOW_MS = 5000;
    var CHECKOUT_GATE_TIMEOUT_MS = 3000;
    var USER_DATA_COOKIE_MAX_AGE = 60 * 60 * 24 * 180;
    var ATTR_COOKIE_MAX_AGE = 60 * 60 * 24 * 90;
    var MAX_ATTR_LEN = 255;
    var CHUNK_MAX_PARTS = 6;
    var SYNC_TIMEOUT_MS = 2500;
    var EVENT_ID_PREFIX = 'sq';
    var EVENT_ID_RANDOM_MAX = 1000000;

    var state = {
        cart: null, fbc: null,
        attributionSynced: null, syncPromise: null, syncFingerprint: null,
        checkoutBypass: false, checkoutGate: false,
        lastCheckoutIntentAt: 0, lastBeginCheckoutAt: 0, lastBeginCheckoutFingerprint: null,
        lastAddToCartAt: 0, lastAddToCartFingerprint: null,
        capturedUserData: null,
        pageViewEmitted: false,
        eventSequence: 0,
        idbSig: null,
        identityRehydrated: false,
        provisionalEid: null,
        _ownCartUpdate: false
    };

    /* ── Utilities ── */
    function toNumber(v) { var n = Number(v); return isNaN(n) ? 0 : n; }
    function getCurrency(cart) {
        // Priority: passed cart > state.cart > gtm presentment > gtm base
        // Covers: mid-session currency switch, initial page load, fallback
        if (cart && cart.currency) return cart.currency;
        if (state.cart && state.cart.currency) return state.cart.currency;
        if (gtm && gtm.presentmentCurrency) return gtm.presentmentCurrency;
        if (gtm && gtm.currency) return gtm.currency;
        return null;
    }

    function getCookie(name) {
        var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
        return m ? decodeURIComponent(m[1]) : null;
    }
    function setCookie(name, value, maxAge) {
        if (!value) return;
        var c = name + '=' + encodeURIComponent(value) + '; path=/; samesite=lax';
        if (maxAge) c += '; max-age=' + maxAge;
        document.cookie = c;
    }
    function getQueryParam(name) {
        if (!window.location || !window.location.search) return null;
        return new URLSearchParams(window.location.search).get(name);
    }
    function getStorage() { try { return window.localStorage || null; } catch (e) { return null; } }
    function getStoredValue(k) { var s = getStorage(); return s ? s.getItem(k) : null; }
    function setStoredValue(k, v, ow) { if (!v) return; var s = getStorage(); if (!s) return; if (!ow && s.getItem(k)) return; s.setItem(k, v); }

    function shopifyRoot() { return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/'; }
    function cartUrl() { return shopifyRoot() + 'cart.js'; }
    function cartUpdateUrl() { return shopifyRoot() + 'cart/update.js'; }

    function nextEventId(eventName) {
        state.eventSequence = (state.eventSequence || 0) + 1;
        if (state.eventSequence > 1000000000) state.eventSequence = 1;
        var ev = String(eventName || 'event').replace(/[^a-zA-Z0-9_]/g, '_');
        return EVENT_ID_PREFIX + '_' + ev + '_' + Date.now() + '_' + state.eventSequence + '_' + Math.floor(Math.random() * EVENT_ID_RANDOM_MAX);
    }

    function mergeObjects(base, over) {
        var out = {};
        if (base && typeof base === 'object') {
            for (var k in base) if (base[k]) out[k] = base[k];
        }
        if (over && typeof over === 'object') {
            for (var j in over) if (over[j]) out[j] = over[j];
        }
        return out;
    }

    function withTimeout(promise, ms) {
        return new Promise(function (resolve, reject) {
            var done = false;
            var timer = setTimeout(function () {
                if (done) return;
                done = true;
                resolve(null);
            }, ms);
            Promise.resolve(promise).then(function (value) {
                if (done) return;
                done = true;
                clearTimeout(timer);
                resolve(value);
            }).catch(function (err) {
                if (done) return;
                done = true;
                clearTimeout(timer);
                reject(err);
            });
        });
    }

    function mergeUserData(existingUserData) {
        var merged = mergeObjects(getUserData(), existingUserData);
        if (!merged || !Object.keys(merged).length) return null;

        if (!merged.external_id || !merged.user_id) {
            var eid = getExternalId();
            if (eid) {
                if (!merged.external_id) merged.external_id = eid;
                if (!merged.user_id) merged.user_id = eid;
            }
        }

        if (!merged.shopify_visitor_id) {
            var vid = getShopifyVisitorId();
            if (vid) merged.shopify_visitor_id = vid;
        }

        return merged;
    }

    function getCurrentPageUrl() {
        return (window.location && window.location.href) || null;
    }

    function applyCurrentUrlContext(payload, opts) {
        if (!payload || typeof payload !== 'object') return;
        opts = opts || {};

        var currentUrl = getCurrentPageUrl();
        if (currentUrl) {
            if (opts.forceCurrentLanding || !payload.landing_page) payload.landing_page = currentUrl;
            if (opts.forceCurrentEventSource || !payload.event_source_url) payload.event_source_url = currentUrl;
        }

        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'campaign_id', 'adset_id', 'ad_id', 'placement', 'fbclid'].forEach(function (key) {
            var value = getQueryParam(key);
            if (value && !payload[key]) payload[key] = value;
        });
    }

    /**
     * v08: promote first-touch PAID identifiers to the top level when the
     * current URL carries none (the "link in bio" last-touch case). Only fills
     * gaps — applyCurrentUrlContext has already set any current-URL values and
     * those always win. This is what carries the original ad's fbc/campaign
     * onto an otherwise-unpaid-looking event so CAPI can still credit the ad.
     */
    function applyPaidAttributionContext(payload) {
        if (!payload || typeof payload !== 'object') return;

        var fbcFirst = getStoredValue('shawq_fbc_first') || getStoredValue('shawq_fbc_last') || getStoredValue('shawq_fbc');
        var fbclidFirst = getStoredValue('shawq_fbclid_first') || getStoredValue('shawq_fbclid_last');
        var campaignFirst = getStoredValue('shawq_campaign_id_first');
        var adsetFirst = getStoredValue('shawq_adset_id_first');
        var adFirst = getStoredValue('shawq_ad_id_first');
        var campaignNameFirst = getQueryParam('utm_campaign') || getStoredValue('shawq_utm_campaign_first');
        var adNameFirst = getQueryParam('utm_content') || getStoredValue('shawq_utm_content_first');

        if (payload.fbc == null && isValidFbc(fbcFirst)) payload.fbc = fbcFirst;
        if (payload.fbclid == null && isValidFbclid(fbclidFirst)) payload.fbclid = fbclidFirst;
        if (payload.fbc_first == null && isValidFbc(fbcFirst)) payload.fbc_first = fbcFirst;
        if (payload.fbclid_first == null && isValidFbclid(fbclidFirst)) payload.fbclid_first = fbclidFirst;
        if (payload.campaign_id == null && campaignFirst) payload.campaign_id = campaignFirst;
        if (payload.adset_id == null && adsetFirst) payload.adset_id = adsetFirst;
        if (payload.ad_id == null && adFirst) payload.ad_id = adFirst;
        if (payload.campaign_name == null && campaignNameFirst) payload.campaign_name = campaignNameFirst;
        if (payload.ad_name == null && adNameFirst) payload.ad_name = adNameFirst;

        var hasPaid = isValidFbc(payload.fbc) || isValidFbclid(payload.fbclid) || isValidFbc(fbcFirst) || isValidFbclid(fbclidFirst);
        if (hasPaid) {
            if (payload.paid_click == null) payload.paid_click = true;
            if (payload.attributed_channel == null) payload.attributed_channel = 'meta_paid';
            // Single inspectable blob: the fbc resolved to its ad. The *_id fields
            // map directly to Meta Ads Manager (campaign/adset/ad); the *_name
            // fields are human-readable IF you tag ad URLs with utm_campaign /
            // utm_content. Lets you SEE which ad an fbc belongs to in the
            // dataLayer / Stape request / Meta Events Manager test events.
            if (payload.meta_ad_attribution == null) {
                payload.meta_ad_attribution = {
                    fbc: payload.fbc || fbcFirst || null,
                    fbclid: payload.fbclid || fbclidFirst || null,
                    campaign_id: payload.campaign_id || campaignFirst || null,
                    adset_id: payload.adset_id || adsetFirst || null,
                    ad_id: payload.ad_id || adFirst || null,
                    campaign_name: payload.campaign_name || null,
                    ad_name: payload.ad_name || null,
                    source: getQueryParam('fbclid') ? 'current_url' : 'first_touch_persisted'
                };
            }
        }
    }

    function enrichStapePayload(payload, opts) {
        if (!payload || typeof payload !== 'object') return payload;
        opts = opts || {};

        var eventName = payload.event || '';
        var isStapeEvent = !!opts.force || (typeof eventName === 'string' && eventName.indexOf('_stape') !== -1);
        if (!isStapeEvent) return payload;

        if (!payload.event_id) payload.event_id = nextEventId(eventName);

        if (payload.ecommerce) {
            if (payload.currency == null && payload.ecommerce.currency) payload.currency = payload.ecommerce.currency;
            if (payload.value == null && payload.ecommerce.value != null) payload.value = payload.ecommerce.value;
        }

        var mergedUserData = mergeUserData(payload.user_data);
        if (mergedUserData) payload.user_data = mergedUserData;

        var mergedAttr = mergeObjects(getAttribution(), payload.attribution);
        if (Object.keys(mergedAttr).length) payload.attribution = mergedAttr;

        if (!payload.fbc && mergedAttr.fbc) payload.fbc = mergedAttr.fbc;
        if (!payload.fbp && mergedAttr.fbp) payload.fbp = mergedAttr.fbp;
        if (!payload.fbclid && mergedAttr.fbclid) payload.fbclid = mergedAttr.fbclid;

        applyCurrentUrlContext(payload);

        // v08: fill first-touch paid identifiers when the current URL is unpaid
        // ("link in bio" last-touch) so the purchase still credits the ad.
        applyPaidAttributionContext(payload);

        var lp = payload.landing_page || mergedAttr.landing_page || mergedAttr.landing_page_last || null;
        var lpAbs = toAbsUrl(lp) || lp;
        if (!payload.landing_page && lpAbs) payload.landing_page = lpAbs;

        if (!payload.event_source_url) {
            var esu = getCurrentPageUrl() || lpAbs || null;
            if (esu) payload.event_source_url = esu;
        }

        // v08: continuously snapshot the identity spine to IDB (deduped by sig).
        persistIdentityToIDB();

        return payload;
    }

    /* ══ GUEST USER DATA CAPTURE ══ */
    function loadCapturedUD() {
        var raw = getCookie('_shawq_ud');
        if (!raw) return null;
        try { return JSON.parse(raw); } catch (e) { return null; }
    }
    function saveCapturedUD(d) { if (d) setCookie('_shawq_ud', JSON.stringify(d), USER_DATA_COOKIE_MAX_AGE); }
    function isEmail(s) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s); }
    function isPhone(s) { return s.replace(/[^0-9]/g, '').length >= 7; }

    // Also persist to IndexedDB (survives Safari ITP 7-day cookie cap)
    function saveToIDB(data, key) {
        key = key || 'user_data';
        try {
            if (!window.indexedDB) return;
            var req = indexedDB.open('shawq_ud', 1);
            req.onupgradeneeded = function (e) { e.target.result.createObjectStore('ud'); };
            req.onsuccess = function (e) {
                try {
                    var db = e.target.result;
                    var tx = db.transaction('ud', 'readwrite');
                    var store = tx.objectStore('ud');
                    var getReq = store.get(key);
                    getReq.onsuccess = function () {
                        var existing = getReq.result;
                        var merged = {};
                        if (existing && typeof existing === 'object') {
                            for (var k in existing) if (existing[k]) merged[k] = existing[k];
                        }
                        if (data && typeof data === 'object') {
                            for (var j in data) if (data[j]) merged[j] = data[j];
                        }
                        store.put(merged, key);
                    };
                    getReq.onerror = function () {
                        store.put(data, key);
                    };
                } catch (ex) { }
            };
        } catch (e) { }
    }

    function loadFromIDB(callback, key) {
        key = key || 'user_data';
        try {
            if (!window.indexedDB) return callback(null);
            var req = indexedDB.open('shawq_ud', 1);
            req.onupgradeneeded = function (e) { e.target.result.createObjectStore('ud'); };
            req.onsuccess = function (e) {
                try {
                    var db = e.target.result;
                    var tx = db.transaction('ud', 'readonly');
                    var get = tx.objectStore('ud').get(key);
                    get.onsuccess = function () { callback(get.result || null); };
                    get.onerror = function () { callback(null); };
                } catch (ex) { callback(null); }
            };
            req.onerror = function () { callback(null); };
        } catch (e) { callback(null); }
    }

    /**
     * IDENTITY + FBC CONTINUITY (v08)
     * Snapshot the durable identity spine (external_id + sticky fbc/fbclid +
     * first-touch paid context) into IDB under a dedicated 'identity' record.
     * Reads cookies/LS directly (never calls getExternalId, so it triggers no
     * id generation as a side effect). Deduped by a signature so the common
     * case writes nothing.
     */
    function snapshotIdentity() {
        var snap = {};
        try {
            var eid = getCookie('_shawq_eid') || getStoredValue('shawq_external_id');
            if (eid) snap.external_id = eid;

            var fbc = getCookie('_fbc');
            if (!isValidFbc(fbc)) fbc = getStoredValue('shawq_fbc_last') || getStoredValue('shawq_fbc_first') || getStoredValue('shawq_fbc');
            if (isValidFbc(fbc)) snap.fbc = fbc;

            var pairs = [
                ['fbc_first', getStoredValue('shawq_fbc_first')],
                ['fbc_last', getStoredValue('shawq_fbc_last')],
                ['fbclid_first', getStoredValue('shawq_fbclid_first')],
                ['fbclid_last', getStoredValue('shawq_fbclid_last')],
                ['campaign_id_first', getStoredValue('shawq_campaign_id_first')],
                ['adset_id_first', getStoredValue('shawq_adset_id_first')],
                ['ad_id_first', getStoredValue('shawq_ad_id_first')],
                ['utm_source_first', getStoredValue('shawq_utm_source_first')],
                ['utm_campaign_first', getStoredValue('shawq_utm_campaign_first')],
                ['utm_content_first', getStoredValue('shawq_utm_content_first')],
                ['landing_page_first', getStoredValue('shawq_landing_page_first')]
            ];
            pairs.forEach(function (p) { if (p[1]) snap[p[0]] = p[1]; });
        } catch (e) { }
        return snap;
    }

    function persistIdentityToIDB() {
        try {
            var snap = snapshotIdentity();
            if (!snap || !Object.keys(snap).length) return;
            var sig = JSON.stringify(snap);
            if (state.idbSig === sig) return; // nothing new to write
            state.idbSig = sig;
            saveToIDB(snap, 'identity');
        } catch (e) { }
    }

    /**
     * On boot, recover the identity spine from IDB ONLY for fields currently
     * missing from cookies/localStorage (e.g. Safari ITP evicted them after the
     * 7-day cap). Never overwrites a value that still exists. Calls back with
     * true if anything was restored, so the caller can force a re-sync.
     */
    function rehydrateIdentityFromIDB(cb) {
        loadFromIDB(function (idb) {
            var restored = false;
            try {
                if (idb && typeof idb === 'object') {
                    // external_id: restore when the canonical layers are empty OR
                    // currently hold a provisional id this page-load just minted
                    // (getExternalId runs before async IDB resolves, e.g. on the
                    // synchronous page_view). The recovered id wins over a
                    // provisional one; a genuine prior id is NEVER clobbered.
                    var curEid = getCookie('_shawq_eid') || getStoredValue('shawq_external_id');
                    if (idb.external_id && (!curEid || curEid === state.provisionalEid)) {
                        setCookie('_shawq_eid', idb.external_id, USER_DATA_COOKIE_MAX_AGE);
                        setStoredValue('shawq_external_id', idb.external_id, true);
                        state.provisionalEid = null;
                        restored = true;
                    }
                    // fbc: restore cookie + LS only if the cookie is missing/invalid.
                    if (idb.fbc && isValidFbc(idb.fbc)) {
                        if (!isValidFbc(getCookie('_fbc'))) { setCookie('_fbc', idb.fbc, ATTR_COOKIE_MAX_AGE); restored = true; }
                        setStoredValue('shawq_fbc', idb.fbc, false);
                    }
                    var ls = [
                        ['shawq_fbc_first', idb.fbc_first || idb.fbc],
                        ['shawq_fbc_last', idb.fbc_last],
                        ['shawq_fbclid_first', idb.fbclid_first],
                        ['shawq_fbclid_last', idb.fbclid_last],
                        ['shawq_campaign_id_first', idb.campaign_id_first],
                        ['shawq_adset_id_first', idb.adset_id_first],
                        ['shawq_ad_id_first', idb.ad_id_first],
                        ['shawq_utm_source_first', idb.utm_source_first],
                        ['shawq_utm_campaign_first', idb.utm_campaign_first],
                        ['shawq_utm_content_first', idb.utm_content_first],
                        ['shawq_landing_page_first', idb.landing_page_first]
                    ];
                    ls.forEach(function (p) {
                        if (p[1] && !getStoredValue(p[0])) { setStoredValue(p[0], p[1], false); restored = true; }
                    });
                }
            } catch (e) { }
            state.identityRehydrated = true;
            if (typeof cb === 'function') cb(restored);
        }, 'identity');
    }

    // Pull from Klaviyo if available
    function getKlaviyoData() {
        var data = {};
        try {
            // Klaviyo's JS API
            if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
                // Can't call identify to GET data, but we can check their cookie
            }
            // Klaviyo stores identified user in _kx cookie (base64 JSON with email)
            var kx = getCookie('_kx');
            if (kx) {
                try {
                    var decoded = JSON.parse(atob(kx));
                    if (decoded && decoded.email && isEmail(decoded.email)) data.email_address = decoded.email;
                    if (decoded && decoded.first_name) data.first_name = decoded.first_name;
                    if (decoded && decoded.last_name) data.last_name = decoded.last_name;
                    if (decoded && decoded.phone_number) data.phone_number = decoded.phone_number;
                } catch (e) { }
            }
            // Also check Klaviyo's __kla_id cookie
            var kla = getCookie('__kla_id');
            if (kla) {
                try {
                    var klaDecoded = JSON.parse(atob(kla));
                    if (klaDecoded) {
                        if (klaDecoded.$email && isEmail(klaDecoded.$email) && !data.email_address) data.email_address = klaDecoded.$email;
                        if (klaDecoded.$first_name && !data.first_name) data.first_name = klaDecoded.$first_name;
                        if (klaDecoded.$last_name && !data.last_name) data.last_name = klaDecoded.$last_name;
                        if (klaDecoded.$phone_number && !data.phone_number) data.phone_number = klaDecoded.$phone_number;
                    }
                } catch (e) { }
            }
        } catch (e) { }
        return Object.keys(data).length ? data : null;
    }

    // Shopify's persistent visitor ID (useful for server-side identity stitching)
    function getShopifyVisitorId() {
        return getCookie('_shopify_y') || null;
    }

    function captureFromInput(input) {
        if (!input || !input.value) return;
        var val = input.value.trim(); if (!val) return;
        var type = (input.type || '').toLowerCase(), name = (input.name || '').toLowerCase();
        var ac = (input.autocomplete || '').toLowerCase(), ph = (input.placeholder || '').toLowerCase();
        var changed = false;
        if (!state.capturedUserData) state.capturedUserData = loadCapturedUD() || {};

        if (type === 'password' || type === 'hidden') return;

        if (type === 'email' || name.indexOf('email') !== -1 || ac.indexOf('email') !== -1 || ph.indexOf('email') !== -1 || isEmail(val)) {
            if (isEmail(val) && state.capturedUserData.email_address !== val) { state.capturedUserData.email_address = val; changed = true; }
        }
        if (type === 'tel' || name.indexOf('phone') !== -1 || ac.indexOf('tel') !== -1 || ph.indexOf('phone') !== -1) {
            if (isPhone(val) && state.capturedUserData.phone_number !== val) { state.capturedUserData.phone_number = val; changed = true; }
        }
        if (name === 'first_name' || name === 'firstname' || ac === 'given-name') {
            if (val && state.capturedUserData.first_name !== val) { state.capturedUserData.first_name = val; changed = true; }
        }
        if (name === 'last_name' || name === 'lastname' || ac === 'family-name') {
            if (val && state.capturedUserData.last_name !== val) { state.capturedUserData.last_name = val; changed = true; }
        }
        if (changed) { saveCapturedUD(state.capturedUserData); saveToIDB(state.capturedUserData); dataLayer.push({ user_data: getUserData() }); }
    }

    function getEventInputTarget(e) {
        if (!e) return null;
        var t = e.target || null;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return t;
        if (e.composedPath) {
            var p = e.composedPath();
            for (var i = 0; i < p.length; i++) {
                var n = p[i];
                if (n && (n.tagName === 'INPUT' || n.tagName === 'TEXTAREA')) return n;
            }
        }
        return null;
    }

    function persistKnownUserData() {
        var consent = getConsent();
        if (consent === 'no') return;
        var liquid = (gtm && gtm.userData) ? gtm.userData : null;
        if (!liquid) return;

        var existing = loadCapturedUD() || {};
        var merged = {};
        var changed = false;
        for (var k in existing) { if (existing[k]) merged[k] = existing[k]; }
        for (var j in liquid) {
            if (liquid[j] && merged[j] !== liquid[j]) { merged[j] = liquid[j]; changed = true; }
        }
        if (changed) { saveCapturedUD(merged); saveToIDB(merged); }
    }

    function initUserDataCapture() {
        state.capturedUserData = loadCapturedUD();

        // Load from IndexedDB as fallback (survives Safari ITP)
        if (!state.capturedUserData || !state.capturedUserData.email_address) {
            loadFromIDB(function (idbData) {
                if (idbData && typeof idbData === 'object') {
                    if (!state.capturedUserData) state.capturedUserData = {};
                    var changed = false;
                    for (var k in idbData) {
                        if (idbData[k] && !state.capturedUserData[k]) { state.capturedUserData[k] = idbData[k]; changed = true; }
                    }
                    if (changed) { saveCapturedUD(state.capturedUserData); dataLayer.push({ user_data: getUserData() }); }
                }
            });
        }

        // Pull from Klaviyo if cookie exists
        var klaviyoData = getKlaviyoData();
        if (klaviyoData) {
            if (!state.capturedUserData) state.capturedUserData = {};
            var kChanged = false;
            for (var kk in klaviyoData) {
                if (klaviyoData[kk] && !state.capturedUserData[kk]) { state.capturedUserData[kk] = klaviyoData[kk]; kChanged = true; }
            }
            if (kChanged) saveCapturedUD(state.capturedUserData);
        }

        // Persist logged-in user data for returning users (if consent allows)
        persistKnownUserData();

        // Blur + change listeners for active input
        document.addEventListener('blur', function (e) { if (e.target && e.target.tagName === 'INPUT') captureFromInput(e.target); }, true);
        document.addEventListener('change', function (e) { if (e.target && e.target.tagName === 'INPUT') captureFromInput(e.target); }, true);
        document.addEventListener('input', function (e) { var t = getEventInputTarget(e); if (t) captureFromInput(t); }, true);

        document.addEventListener('submit', function (e) {
            var form = e.target; if (!form || !form.querySelectorAll) return;
            var inputs = form.querySelectorAll('input[type="email"], input[type="tel"], input[name*="email"], input[name*="phone"], input[name*="first"], input[name*="last"], input[autocomplete*="email"], input[autocomplete*="tel"], input[autocomplete*="given-name"], input[autocomplete*="family-name"], textarea');
            for (var i = 0; i < inputs.length; i++) captureFromInput(inputs[i]);
        }, true);

        // Scan for autofilled inputs (browser fills forms before user interaction)
        function scanAutofilled() {
            var inputs = document.querySelectorAll('input[type="email"], input[type="tel"], input[name*="email"], input[name*="phone"], input[name*="first"], input[name*="last"], input[autocomplete*="email"], input[autocomplete*="tel"], input[autocomplete*="given-name"], input[autocomplete*="family-name"], textarea');
            for (var i = 0; i < inputs.length; i++) {
                if (inputs[i].value && inputs[i].value.trim()) captureFromInput(inputs[i]);
            }
        }

        // Scan immediately, after DOM ready, and after a delay (autofill can be async)
        scanAutofilled();
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', scanAutofilled);
        }
        setTimeout(scanAutofilled, 1500);
        setTimeout(scanAutofilled, 4000);

        // Re-scan when new forms are injected (popups, shadow UI)
        if (typeof MutationObserver === 'function') {
            var scanTimer = null;
            var scheduleScan = function () {
                if (scanTimer) return;
                scanTimer = setTimeout(function () { scanTimer = null; scanAutofilled(); }, 600);
            };
            var obs = new MutationObserver(function (m) {
                for (var i = 0; i < m.length; i++) {
                    if (m[i].addedNodes && m[i].addedNodes.length) { scheduleScan(); break; }
                }
            });
            obs.observe(document.documentElement || document.body, { childList: true, subtree: true });
        }
    }

    /**
     * External ID — Meta requires this on every event, especially purchase.
     * For logged-in: customer.id (stable, known)
     * For guests: generate a UUID, persist in cookie + localStorage + IndexedDB
     * Key: must be the SAME value across browser pixel and CAPI for dedup
     */
    function generateUUID() {
        // crypto.randomUUID where available, otherwise manual
        try { if (crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (e) { }
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });
    }

    function getExternalId() {
        // 1. Logged-in customer ID is best (stable across devices)
        var liquid = (gtm && gtm.userData) ? gtm.userData : null;
        if (liquid && liquid.user_id) return String(liquid.user_id);

        // 2. Try existing persisted guest ID
        var eid = getCookie('_shawq_eid') || getStoredValue('shawq_external_id') || null;
        if (eid) {
            // Re-persist in case one storage expired (Safari ITP)
            setCookie('_shawq_eid', eid, USER_DATA_COOKIE_MAX_AGE);
            setStoredValue('shawq_external_id', eid, true);
            return eid;
        }

        // 3. Generate new guest ID
        eid = generateUUID();
        setCookie('_shawq_eid', eid, USER_DATA_COOKIE_MAX_AGE);
        setStoredValue('shawq_external_id', eid, true);
        saveToIDB({ external_id: eid }); // also persist to IDB for Safari
        state.provisionalEid = eid; // provisional: IDB rehydration may replace it with a recovered id
        return eid;
    }

    function getCartAttrUserData(cart) {
        var attrs = cart && cart.attributes ? cart.attributes : null;
        if (!attrs) return null;
        var out = {};
        if (attrs._shawq_email) out.email_address = attrs._shawq_email;
        if (attrs._shawq_phone) out.phone_number = attrs._shawq_phone;
        if (attrs._shawq_fn) out.first_name = attrs._shawq_fn;
        if (attrs._shawq_ln) out.last_name = attrs._shawq_ln;
        if (attrs._shawq_uid) out.user_id = attrs._shawq_uid;
        if (attrs._shawq_city) out.city = attrs._shawq_city;
        if (attrs._shawq_region) out.region = attrs._shawq_region;
        if (attrs._shawq_country) out.country = attrs._shawq_country;
        if (attrs._shawq_zip) out.postal_code = attrs._shawq_zip;
        if (attrs._shawq_street) out.street = attrs._shawq_street;
        if (attrs._shawq_eid) out.external_id = attrs._shawq_eid;
        return Object.keys(out).length ? out : null;
    }

    function getUserData() {
        // Priority (lowest → highest, later overwrites):
        // 1. Klaviyo cookie  2. Cart attributes  3. IDB/cookie captured  4. Liquid (logged-in)
        var klaviyo = getKlaviyoData();
        var captured = state.capturedUserData || loadCapturedUD();
        var cartUD = getCartAttrUserData(state.cart || (gtm && gtm.cart));
        var liquid = (gtm && gtm.userData) ? gtm.userData : null;
        var merged = {};

        if (klaviyo) { for (var a in klaviyo) { if (klaviyo[a]) merged[a] = klaviyo[a]; } }
        if (cartUD) { for (var d in cartUD) { if (cartUD[d]) merged[d] = cartUD[d]; } }
        if (captured) { for (var b in captured) { if (captured[b]) merged[b] = captured[b]; } }
        if (liquid) { for (var c in liquid) { if (liquid[c]) merged[c] = liquid[c]; } }

        // Shopify visitor ID for server-side identity stitching
        var vid = getShopifyVisitorId();
        if (vid) merged.shopify_visitor_id = vid;

        // External ID — always present, guest or logged-in
        // Push as BOTH user_id and external_id so Stape can map either field
        var eid = getExternalId();
        if (eid) {
            merged.external_id = eid;
            if (!merged.user_id) merged.user_id = eid;
        }

        return Object.keys(merged).length ? merged : null;
    }

    /* ══ FBP / FBC ══ */
    function isValidFbclid(v) { var s = String(v || '').trim(); return !!(s && s.length >= 8 && s.length <= 500 && /^[A-Za-z0-9._:-]+$/.test(s)); }
    function isValidFbp(v) { return /^fb\.1\.\d{10,16}\.\d{6,20}$/.test(String(v || '').trim()); }
    function isValidFbc(v) { return /^fb\.1\.\d{10,16}\.[A-Za-z0-9._:-]{8,500}$/.test(String(v || '').trim()); }
    function buildFbc(fbclid) { return isValidFbclid(fbclid) ? 'fb.1.' + Math.floor(Date.now() / 1000) + '.' + fbclid : null; }
    function buildFbp() { return 'fb.1.' + Date.now() + '.' + Math.floor(1e9 + Math.random() * 9e9); }
    function getFbp() {
        var fbp = getCookie('_fbp');
        if (isValidFbp(fbp)) return fbp;

        var storedFbp = getStoredValue('shawq_fbp');
        if (isValidFbp(storedFbp)) {
            setCookie('_fbp', storedFbp, ATTR_COOKIE_MAX_AGE);
            return storedFbp;
        }

        fbp = buildFbp();
        if (fbp) {
            setCookie('_fbp', fbp, ATTR_COOKIE_MAX_AGE);
            setStoredValue('shawq_fbp', fbp, true);
        }
        return fbp || null;
    }
    function getFbclidParam() { return getQueryParam('fbclid'); }
    function getFbc() {
        var fbclid = getQueryParam('fbclid');
        var fbcCookie = getCookie('_fbc');
        var storedFbc = getStoredValue('shawq_fbc_last') || getStoredValue('shawq_fbc_first') || getStoredValue('shawq_fbc');
        var fbc = isValidFbc(fbcCookie) ? fbcCookie : null;

        if (isValidFbclid(fbclid)) {
            var n = buildFbc(fbclid);
            if (n) {
                fbc = n;
                if (n !== fbcCookie) setCookie('_fbc', n, ATTR_COOKIE_MAX_AGE);
                setStoredValue('shawq_fbc', n, true);
            }
        } else if (!fbc && isValidFbc(storedFbc)) {
            fbc = storedFbc;
            setCookie('_fbc', storedFbc, ATTR_COOKIE_MAX_AGE);
        }

        if (fbc) state.fbc = fbc;
        return fbc || null;
    }

    /* ══ TOUCH TRACKING ══ */
    function getTF(k) { return getStoredValue('shawq_' + k + '_first'); }
    function getTL(k) { return getStoredValue('shawq_' + k + '_last'); }
    function setTF(k, v) { setStoredValue('shawq_' + k + '_first', v); }
    function setTL(k, v) { setStoredValue('shawq_' + k + '_last', v, true); }

    function getExternalReferrer() {
        var r = document.referrer || null;
        if (r && window.location && r.indexOf(window.location.origin) === 0) r = null;
        return r || null;
    }
    function getLandingPage() {
        return window.location ? window.location.pathname + window.location.search : null;
    }
    function toAbsUrl(v) {
        if (!v) return null;
        var s = String(v);
        if (/^https?:\/\//i.test(s)) return s;
        if (s.charAt(0) === '/' && window.location && window.location.origin) return window.location.origin + s;
        return null;
    }
    function getConsent() {
        var p = window.Shopify && Shopify.customerPrivacy; if (!p) return null;
        if (typeof p.getTrackingConsent === 'function') { var c = p.getTrackingConsent(); if (c === 'yes' || c === 'no' || c === 'pending') return c; if (typeof c === 'boolean') return c ? 'yes' : 'no'; }
        if (typeof p.userCanBeTracked === 'function') return p.userCanBeTracked() ? 'yes' : 'no';
        return null;
    }

    /* ══ ATTRIBUTION ══ */
    function getAttribution() {
        var a = {};
        var fbclidP = getFbclidParam(), utmS = getQueryParam('utm_source'), utmM = getQueryParam('utm_medium');
        var utmC = getQueryParam('utm_campaign'), utmCo = getQueryParam('utm_content'), utmT = getQueryParam('utm_term');
        var utmId = getQueryParam('utm_id'), campaignId = getQueryParam('campaign_id'), adsetId = getQueryParam('adset_id'), adId = getQueryParam('ad_id'), placement = getQueryParam('placement');
        var extRef = getExternalReferrer();
        var touch = !!(fbclidP || utmS || utmM || utmC || utmCo || utmT || utmId || campaignId || adsetId || adId || placement || extRef);

        [['fbclid', fbclidP], ['utm_source', utmS], ['utm_medium', utmM], ['utm_campaign', utmC], ['utm_content', utmCo], ['utm_term', utmT], ['utm_id', utmId], ['campaign_id', campaignId], ['adset_id', adsetId], ['ad_id', adId], ['placement', placement], ['referrer', extRef]].forEach(function (p) {
            if (p[1]) { setTF(p[0], p[1]); setTL(p[0], p[1]); }
        });

        var lp = getLandingPage();
        if (lp) { setTF('landing_page', lp); if (touch) setTL('landing_page', lp); }

        var fbc = getFbc();
        if (fbc) { setTF('fbc', fbc); if (fbclidP) setTL('fbc', fbc); if (!getTL('fbc')) setTL('fbc', fbc); }

        var fbp = getFbp(), consent = getConsent();

        ['fbc', 'fbclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'campaign_id', 'adset_id', 'ad_id', 'placement', 'landing_page', 'referrer'].forEach(function (k) {
            var first = getTF(k), last = getTL(k) || first;
            if (k === 'landing_page' && lp) a[k] = lp;
            else if (last) a[k] = last;
            if (first) a[k + '_first'] = first;
            if (last) a[k + '_last'] = last;
        });

        if (fbp) a.fbp = fbp;
        if (consent) a.consent = consent;
        return a;
    }

    /* ══ CART ATTRIBUTE CHUNKING ══ */
    function normAttr(v) { if (v == null) return null; var s = String(v); if (!s) return null; return s.length > MAX_ATTR_LEN ? s.slice(0, MAX_ATTR_LEN) : s; }
    function chunkAttr(v) {
        if (v == null) return null; var s = String(v); if (!s) return null;
        var p = []; for (var i = 0; i < s.length && p.length < CHUNK_MAX_PARTS; i += MAX_ATTR_LEN) p.push(s.slice(i, i + MAX_ATTR_LEN));
        return p.length ? p : null;
    }
    function clearChunked(attrs, key) {
        attrs[key] = '';
        attrs[key + '_parts'] = '';
        for (var i = 2; i <= CHUNK_MAX_PARTS; i++) attrs[key + '_p' + i] = '';
    }
    function setChunked(attrs, key, val) {
        clearChunked(attrs, key);
        var p = chunkAttr(val); if (!p) return;
        attrs[key] = p[0]; attrs[key + '_parts'] = String(p.length);
        for (var i = 2; i <= p.length; i++) attrs[key + '_p' + i] = p[i - 1];
    }

    function buildAttrAttributes() {
        var attr = getAttribution(), out = {};
        var chunkedKeys = {
            fbc: 1, fbc_first: 1, fbc_last: 1,
            fbclid: 1, fbclid_first: 1, fbclid_last: 1,
            landing_page: 1, landing_page_first: 1, landing_page_last: 1
        };
        Object.keys(attr).forEach(function (k) {
            if (chunkedKeys[k]) { setChunked(out, k, attr[k]); return; }
            var v = normAttr(attr[k]); if (v != null) out[k] = v;
        });
        // Sync user data to cart attributes for the pixel
        var ud = getUserData();
        if (ud) {
            if (ud.email_address) out['_shawq_email'] = normAttr(ud.email_address);
            if (ud.phone_number) out['_shawq_phone'] = normAttr(ud.phone_number);
            if (ud.first_name) out['_shawq_fn'] = normAttr(ud.first_name);
            if (ud.last_name) out['_shawq_ln'] = normAttr(ud.last_name);
            if (ud.user_id) out['_shawq_uid'] = normAttr(ud.user_id);
            if (ud.city) out['_shawq_city'] = normAttr(ud.city);
            if (ud.region) out['_shawq_region'] = normAttr(ud.region);
            if (ud.country) out['_shawq_country'] = normAttr(ud.country);
            if (ud.postal_code) out['_shawq_zip'] = normAttr(ud.postal_code);
            if (ud.street) out['_shawq_street'] = normAttr(ud.street);
            if (ud.external_id) out['_shawq_eid'] = normAttr(ud.external_id);
        }
        // Always sync the presentment currency so pixel never has missing currency
        var cur = getCurrency(null);
        if (cur) out['_shawq_currency'] = cur;
        return out;
    }

    /* ══ CART ITEM MAPPING ══ */
    function itemCents(item) {
        if (!item) return 0;
        if (typeof item.final_price === 'number') return item.final_price;
        if (typeof item.price === 'number') return item.price;
        return toNumber(item.final_price || item.price || 0);
    }
    function mapItem(item, qo) {
        var q = qo == null ? toNumber(item.quantity) : toNumber(qo);
        return {
            item_id: item.variant_id || item.id, item_name: item.product_title || item.title,
            item_brand: item.vendor || null, item_category: item.product_type || null,
            item_variant: item.variant_title || null, price: itemCents(item) / 100, quantity: q
        };
    }
    function sumVal(items) { return items.reduce(function (a, i) { return a + (toNumber(i.price) * toNumber(i.quantity || 1)); }, 0); }

    /* ══ DATALAYER PUSH ══ */
    function pushEvent(name, items, cart, extraEcom, extraPL) {
        if (!items || !items.length) return;

        // Dedup add_to_cart_stape / remove_from_cart_stape — fetch interceptor + DOM events can both fire
        if (name === 'add_to_cart_stape' || name === 'remove_from_cart_stape') {
            var now = Date.now();
            var fp = name + '|' + items.map(function (i) { return (i.item_id || '') + 'x' + (i.quantity || 1); }).join(',');
            if ((now - state.lastAddToCartAt) < 3000 && state.lastAddToCartFingerprint === fp) return;
            state.lastAddToCartAt = now;
            state.lastAddToCartFingerprint = fp;
        }

        var ec = { currency: getCurrency(cart), value: sumVal(items), items: items };
        if (extraEcom) Object.keys(extraEcom).forEach(function (k) { ec[k] = extraEcom[k]; });
        dataLayer.push({ ecommerce: null });
        var pl = { event: name, ecommerce: ec, currency: ec.currency, value: ec.value };
        var ud = getUserData(); if (ud) pl.user_data = ud;
        var at = getAttribution();
        if (at.fbc) pl.fbc = at.fbc; if (at.fbp) pl.fbp = at.fbp; if (at.fbclid) pl.fbclid = at.fbclid;
        if (Object.keys(at).length) pl.attribution = at;
        applyCurrentUrlContext(pl, { forceCurrentLanding: true, forceCurrentEventSource: true });
        if (extraPL && typeof extraPL === 'object') Object.keys(extraPL).forEach(function (k) { pl[k] = extraPL[k]; });
        enrichStapePayload(pl, { force: true });
        dataLayer.push(pl);
    }

    function pushPixelEvent(pl) {
        if (!pl || !pl.event) return;
        if (pl.ecommerce) dataLayer.push({ ecommerce: null });
        if (pl.ecommerce) {
            if (pl.currency == null && pl.ecommerce.currency) pl.currency = pl.ecommerce.currency;
            if (pl.value == null && pl.ecommerce.value != null) pl.value = pl.ecommerce.value;
        }
        enrichStapePayload(pl);
        dataLayer.push(pl);
    }

    function hasEventInDataLayer(eventName) {
        for (var i = 0; i < dataLayer.length; i++) {
            var entry = dataLayer[i];
            if (entry && typeof entry === 'object' && entry.event === eventName) return true;
        }
        return false;
    }

    function emitPageView() {
        if (state.pageViewEmitted || hasEventInDataLayer('page_view_stape')) {
            state.pageViewEmitted = true;
            return false;
        }

        var payload = {
            event: 'page_view_stape',
            page_location: (window.location && window.location.href) || null,
            page_path: (window.location && (window.location.pathname + window.location.search)) || null,
            page_title: document && document.title ? document.title : null
        };
        var currency = getCurrency(state.cart || null);
        if (currency) payload.currency = currency;

        pushPixelEvent(payload);
        state.pageViewEmitted = true;
        return true;
    }

    /* ══ BEGIN CHECKOUT ══ */
    function ckFingerprint(c) {
        if (!c) return '';
        return [c.token || c.cart_id || '', toNumber(c.item_count || ((c.items && c.items.length) || 0)), toNumber(c.total_price)].join('|');
    }

    function pushBeginCheckout(cart, src, opts) {
        opts = opts || {};
        var has = !!(cart && cart.items && cart.items.length), now = Date.now();
        var fp = has ? ckFingerprint(cart) : ['fallback', (window.location && window.location.pathname) || ''].join('|');
        if ((now - state.lastBeginCheckoutAt) <= BEGIN_CHECKOUT_DEDUP_WINDOW_MS && state.lastBeginCheckoutFingerprint === fp) return false;
        state.lastBeginCheckoutAt = now; state.lastBeginCheckoutFingerprint = fp;
        if (has) {
            var items = cart.items.map(function (i) { return mapItem(i); }), tv = toNumber(cart.total_price) / 100;
            pushEvent('begin_checkout_stape', items, cart, { value: tv }, { checkout_source: src || 'unknown', checkout_origin: 'gtm_events_js' });
            // Write timestamp so pixel can dedup (skip begin_checkout if theme already fired it)
            updateCartAttrs({ _shawq_bc_at: String(Math.floor(Date.now() / 1000)) }, { keepalive: true });
            return true;
        }
        if (!opts.allowFallback) return false;
        var p = { event: 'begin_checkout_stape', checkout_source: src || 'unknown', checkout_origin: 'gtm_events_js' };
        var cur = getCurrency(cart || state.cart); if (cur) p.currency = cur;
        pushPixelEvent(p);
        return true;
    }

    function getProductPageEventItem() {
        var vid = getVariantId();
        if (!vid && !(gtm && gtm.product && gtm.product.id)) return null;

        var qty = getVariantQty();
        return {
            item_id: vid || (gtm.product && gtm.product.id),
            item_name: (gtm.product && gtm.product.name) || '',
            item_brand: (gtm.product && gtm.product.brand) || null,
            item_category: (gtm.product && gtm.product.category) || null,
            item_variant: (gtm.product && gtm.product.variant) || null,
            price: toNumber(gtm && gtm.product ? gtm.product.price : 0),
            quantity: qty || 1
        };
    }

    function pushBeginCheckoutFromProduct(src) {
        var item = getProductPageEventItem();
        if (!item) return false;

        pushEvent('begin_checkout_stape', [item], state.cart || null, {
            value: toNumber(item.price) * toNumber(item.quantity || 1)
        }, {
            checkout_source: src || 'dynamic_checkout',
            checkout_origin: 'gtm_events_js',
            checkout_mode: 'dynamic_checkout_product'
        });
        return true;
    }

    function pushTrackingAddToCartFromProduct(src) {
        var item = getProductPageEventItem();
        if (!item) return false;

        pushEvent('add_to_cart_stape', [item], state.cart || null, {
            value: toNumber(item.price) * toNumber(item.quantity || 1)
        }, {
            add_to_cart_source: src || 'dynamic_checkout',
            add_to_cart_origin: 'gtm_events_js',
            add_to_cart_mode: 'dynamic_checkout_tracking_only'
        });
        return true;
    }

    /* ══ CHECKOUT HELPERS ══ */
    function isCheckoutHref(h) { if (!h) return false; var n = String(h).toLowerCase(); return n.indexOf('/checkout') !== -1 || n.indexOf('/checkouts') !== -1; }
    function getFormAction(f) { return f ? (f.getAttribute ? f.getAttribute('action') : '') || f.action || '' : ''; }
    function findCheckoutLink(t) {
        if (!t || !t.closest) return null;
        var l = t.closest('a[href]'); if (!l) return null;
        var h = l.getAttribute('href') || ''; return isCheckoutHref(h) ? l : null;
    }
    function isCheckoutElement(t) {
        if (!t || !t.closest) return false;
        if (t.closest(CHECKOUT_BUTTON_SELECTOR) || t.closest(DYNAMIC_CHECKOUT_SELECTOR)) return true;
        return !!findCheckoutLink(t);
    }
    function markIntent() { state.lastCheckoutIntentAt = Date.now(); }
    function isDynIframe(el) { return !!(el && el.tagName === 'IFRAME' && el.closest && el.closest('.shopify-payment-button')); }

    /* ══ CART SYNC ══ */
    function updateCartAttrs(attrs, opts) {
        opts = opts || {};
        state._ownCartUpdate = true;
        return fetch(cartUpdateUrl(), { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: attrs }), keepalive: !!opts.keepalive })
            .then(function (r) { return r.json(); }).then(function (c) { state._ownCartUpdate = false; state.cart = c; return c; }).catch(function () { state._ownCartUpdate = false; return null; });
    }

    function syncAttribution(cart, opts) {
        opts = opts || {};
        var attrs = buildAttrAttributes(), keys = Object.keys(attrs);
        if (!keys.length) return Promise.resolve(cart || state.cart || null);
        if (window.shawqGtm) window.shawqGtm.attribution = attrs;

        var existing = cart && cart.attributes ? cart.attributes : null, need = !existing;
        if (existing) keys.forEach(function (k) { if (existing[k] !== attrs[k]) need = true; });

        var fp = JSON.stringify(attrs);
        if (!opts.force && !need) { state.attributionSynced = fp; return Promise.resolve(cart || state.cart || null); }
        if (!opts.force && state.attributionSynced === fp) return Promise.resolve(cart || state.cart || null);
        if (state.syncPromise && state.syncFingerprint === fp) return state.syncPromise;

        state.syncFingerprint = fp;
        state.syncPromise = withTimeout(updateCartAttrs(attrs, { keepalive: !!opts.keepalive }), SYNC_TIMEOUT_MS)
            .then(function (u) {
                if (u) state.attributionSynced = fp;
                return u || cart || state.cart || null;
            })
            .catch(function () { return cart || state.cart || null; })
            .finally(function () {
                state.syncPromise = null;
                state.syncFingerprint = null;
            });
        return state.syncPromise;
    }

    /* ══ SHOP PAY BUY NOW GUARD ══ */
    function getVariantId() {
        var forms = document.querySelectorAll('form[action*="/cart/add"]');
        for (var i = 0; i < forms.length; i++) {
            var id = forms[i].querySelector('input[name="id"], select[name="id"]');
            if (id && id.value) return id.value;
        }
        return null;
    }

    function getVariantQty() {
        var forms = document.querySelectorAll('form[action*="/cart/add"]');
        for (var i = 0; i < forms.length; i++) {
            var q = forms[i].querySelector('input[name="quantity"], select[name="quantity"]');
            if (q && q.value) return toNumber(q.value) || 1;
        }
        return 1;
    }

    function getFormVariantId(form) {
        if (!form || !form.querySelector) return null;
        var id = form.querySelector('input[name="id"], select[name="id"]');
        return (id && id.value) ? id.value : null;
    }

    function getFormQty(form) {
        if (!form || !form.querySelector) return 1;
        var q = form.querySelector('input[name="quantity"], select[name="quantity"]');
        return (q && q.value) ? (toNumber(q.value) || 1) : 1;
    }

    function pushAddToCartFromForm(form, src) {
        var vid = getFormVariantId(form) || getVariantId();
        var qty = getFormQty(form);
        if (!vid && !(gtm && gtm.product && gtm.product.id)) return;

        var item = {
            item_id: vid || (gtm.product && gtm.product.id),
            item_name: (gtm.product && gtm.product.name) || '',
            item_brand: (gtm.product && gtm.product.brand) || null,
            item_category: (gtm.product && gtm.product.category) || null,
            item_variant: (gtm.product && gtm.product.variant) || null,
            price: toNumber(gtm && gtm.product ? gtm.product.price : 0),
            quantity: qty || 1
        };

        pushEvent('add_to_cart_stape', [item], state.cart || null, { value: toNumber(item.price) * toNumber(item.quantity || 1) }, { add_to_cart_source: src || 'form_submit' });
    }

    function handleDynIntent(src) {
        var now = Date.now();
        if ((now - (state._lastDynIntentAt || 0)) < 3000) return;  // 3s dedup: pointerdown/mousedown/touchstart/click can all fire for one tap.
        state._lastDynIntentAt = now;
        markIntent();
        // v08: a Shop Pay / "Buy Now" tap can navigate away before the next
        // page can persist anything. Snapshot the identity spine (external_id +
        // fbc + first-touch ad context) to IDB right now so it survives the jump
        // straight to the accelerated-checkout flow.
        persistIdentityToIDB();

        var isProductPage = !!(document.querySelector('form[action*="/cart/add"]') || (window.location.pathname.indexOf('/products/') !== -1));
        var sync = syncAttribution(state.cart || null, { keepalive: true, force: true });

        if (isProductPage) {
            pushTrackingAddToCartFromProduct(src);
            if (state.cart && state.cart.items && state.cart.items.length) {
                sync.then(function (c) { if (c) pushBeginCheckout(c, src + '_sync', { allowFallback: true }); }).catch(function () { });
            } else {
                pushBeginCheckoutFromProduct(src);
                sync.catch(function () { });
            }
        } else {
            sync.then(function (c) { if (c) pushBeginCheckout(c, src + '_sync', { allowFallback: true }); }).catch(function () { });
        }
    }

    /* ══ DYNAMIC CHECKOUT BINDINGS ══ */
    function bindDynContainer(el) {
        if (!el || el.__shawqBound) return; el.__shawqBound = true;
        var fire = function () { handleDynIntent('dynamic_container'); };
        el.addEventListener('pointerdown', fire, true); el.addEventListener('mousedown', fire, true);
        el.addEventListener('touchstart', fire, true); el.addEventListener('click', fire, true);
    }
    function bindDynContainers(root) {
        if (!root || !root.querySelectorAll) return;
        var c = root.querySelectorAll(DYNAMIC_CHECKOUT_CONTAINER_SELECTOR);
        for (var i = 0; i < c.length; i++) bindDynContainer(c[i]);
    }
    function initDynBindings() {
        bindDynContainers(document);
        if (typeof MutationObserver !== 'function') return;
        var obs = new MutationObserver(function (m) {
            for (var i = 0; i < m.length; i++) {
                var added = m[i].addedNodes || [];
                for (var j = 0; j < added.length; j++) {
                    var n = added[j]; if (!n || n.nodeType !== 1) continue;
                    if (n.matches && n.matches(DYNAMIC_CHECKOUT_CONTAINER_SELECTOR)) bindDynContainer(n);
                    bindDynContainers(n);
                }
            }
        });
        obs.observe(document.documentElement || document.body, { childList: true, subtree: true });
    }

    /* ══ PIXEL BRIDGE ══ */
    function handleBridge(e) {
        if (!e || !e.data) return;
        if (e.origin && e.origin !== window.location.origin) return;
        var d = e.data; if (d.type !== 'shawq-gtm') return;
        var n = d.event || d.event_name; if (!n) return;
        var pl = { event: n }; if (d.ecommerce) pl.ecommerce = d.ecommerce; if (d.user_data) pl.user_data = d.user_data;
        pushPixelEvent(pl);
    }

    /* ══ CART DIFF ══ */
    function indexByKey(items) { var m = {}; (items || []).forEach(function (i) { if (i && i.key) m[i.key] = i; }); return m; }
    function diffCart(prev, next) {
        var added = [], removed = [], pm = indexByKey(prev.items), nm = indexByKey(next.items);
        next.items.forEach(function (i) { var p = pm[i.key], pq = p ? toNumber(p.quantity) : 0, nq = toNumber(i.quantity); if (nq > pq) added.push(mapItem(i, nq - pq)); });
        prev.items.forEach(function (i) { var n = nm[i.key], pq = toNumber(i.quantity), nq = n ? toNumber(n.quantity) : 0; if (pq > nq) removed.push(mapItem(i, pq - nq)); });
        return { added: added, removed: removed };
    }

    /* ═══════════════════════════════════════════════════
       INITIALIZATION
       ═══════════════════════════════════════════════════ */
    // v08: recover the identity spine (external_id + fbc) from IndexedDB BEFORE
    // the first attribution sync, so a returning paid visitor keeps the same
    // external_id / fbc instead of getExternalId() minting a fresh guest id.
    // Boot is never blocked on IDB: a short timeout guarantees init still runs.
    (function bootWithIdentityRecovery() {
        var started = false;
        function startInit() {
            if (started) return;
            started = true;
            initUserDataCapture();
            syncAttribution(null);
        }
        try {
            rehydrateIdentityFromIDB(function (restored) {
                var already = started;
                startInit();
                // If IDB was slow and init already ran without the recovered
                // identity, force a re-sync so cart attributes carry it.
                if (restored && already) { try { syncAttribution(null, { force: true }); } catch (e) { } }
            });
        } catch (e) { startInit(); }
        setTimeout(startInit, 800);
    })();

    // ── dataLayer.push interceptor ──
    // Only enriches third-party events that Stape sGTM consumes.
    // Without this, Stape app's page_view_stape and Google channel's view_item
    // arrive at sGTM with sparse/empty user_data, losing attribution.
    (function () {
        if (dataLayer.push && dataLayer.push.__shawqWrapped) return;
        var origPush = dataLayer.push.bind(dataLayer);
        var intercepting = false;

        // Events from third-party scripts that Stape consumes but ship with sparse data
        var ENRICH_EVENTS = {
            'page_view_stape': 1,
            'view_item': 1
        };

        function wrappedPush() {
            var forwarded = [];

            if (intercepting) return origPush.apply(dataLayer, arguments);
            intercepting = true;
            try {
                for (var i = 0; i < arguments.length; i++) {
                    var obj = arguments[i];
                    if (!obj || typeof obj !== 'object' || !obj.event) {
                        forwarded.push(obj);
                        continue;
                    }
                    var ev = obj.event;
                    if (ev === 'page_view_stape') {
                        if (state.pageViewEmitted || hasEventInDataLayer('page_view_stape')) continue;
                        state.pageViewEmitted = true;
                    }
                    if (!ENRICH_EVENTS[ev] && String(ev).indexOf('_stape') === -1) {
                        forwarded.push(obj);
                        continue;
                    }
                    var cloned = {};
                    for (var k in obj) cloned[k] = obj[k];
                    enrichStapePayload(cloned, { force: !!ENRICH_EVENTS[ev] });
                    forwarded.push(cloned);
                }
                if (!forwarded.length) return dataLayer.length;
                return origPush.apply(dataLayer, forwarded);
            } finally {
                intercepting = false;
            }
        }

        wrappedPush.__shawqWrapped = true;
        dataLayer.push = wrappedPush;
    })();

    function ensureInitialCart() {
        if (state.cart) return;
        if (gtm && gtm.cart && gtm.cart.items) {
            if (gtm.cart.items.length === 0 || gtm.cart.items[0].key) { state.cart = gtm.cart; syncAttribution(state.cart); return; }
        }
        fetch(cartUrl(), { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (c) { if (!state.cart) state.cart = c; syncAttribution(c); }).catch(function () { });
    }
    ensureInitialCart();
    initDynBindings();
    emitPageView();

    /* ═══════════════════════════════════════════════════
       VIEW CONTENT — fires once on product pages
       ═══════════════════════════════════════════════════ */
    if (gtm.product && gtm.product.id) {
        var prodItem = {
            item_id: gtm.product.id,
            item_name: gtm.product.name || '',
            item_brand: gtm.product.brand || null,
            item_category: gtm.product.category || null,
            item_variant: gtm.product.variant || null,
            price: toNumber(gtm.product.price),
            quantity: 1
        };
        var prodValue = toNumber(gtm.product.price);
        pushEvent('view_item_stape', [prodItem], state.cart || null, { value: prodValue });
    }

    /* ═══════════════════════════════════════════════════
       CART API INTERCEPTION
       ═══════════════════════════════════════════════════
       Themes don't all dispatch cart:change / variant:add.
       Instead, intercept fetch() to /cart/add.js, /cart/change.js,
       /cart/update.js, /cart/clear.js — then diff the cart.
       ═══════════════════════════════════════════════════ */
    var CART_API_PATTERN = /\/cart\/(add|change|update|clear)\.js/;
    var _origFetch = window.fetch;

    function refreshCartAndDiff(action) {
        var prevCart = state.cart;
        return _origFetch.call(window, cartUrl(), { credentials: 'same-origin' })
            .then(function (r) { return r.json(); })
            .then(function (newCart) {
                if (!newCart || !newCart.items) return;
                state.cart = newCart;
                syncAttribution(newCart);

                if (!prevCart || !prevCart.items) {
                    // First cart load — treat all items as added if this was an add action
                    if (action === 'add' && newCart.items.length) {
                        pushEvent('add_to_cart_stape', newCart.items.map(function (i) { return mapItem(i); }), newCart);
                    }
                    return;
                }

                var diff = diffCart(prevCart, newCart);
                if (diff.added.length) pushEvent('add_to_cart_stape', diff.added, newCart);
                if (diff.removed.length) pushEvent('remove_from_cart_stape', diff.removed, newCart);
            })
            .catch(function () { });
    }

    window.fetch = function (url, opts) {
        var urlStr = (typeof url === 'string') ? url : (url && url.url ? url.url : '');
        var match = urlStr.match(CART_API_PATTERN);

        if (!match) return _origFetch.apply(window, arguments);

        // Skip our own attribute-only updates
        if (state._ownCartUpdate && match[1] === 'update') return _origFetch.apply(window, arguments);

        var action = match[1]; // 'add', 'change', 'update', or 'clear'

        return _origFetch.apply(window, arguments).then(function (response) {
            // Clone so the original caller still gets their response
            var cloned = response.clone();
            // After response settles, refresh cart and diff
            cloned.json()
                .then(function () { refreshCartAndDiff(action); })
                .catch(function () { refreshCartAndDiff(action); });
            return response;
        });
    };

    // Also intercept XMLHttpRequest for themes using jQuery.ajax
    var _origXHROpen = XMLHttpRequest.prototype.open;
    var _origXHRSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
        this._shawqUrl = url;
        return _origXHROpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
        var xhr = this;
        var urlStr = xhr._shawqUrl || '';
        var match = urlStr.match(CART_API_PATTERN);

        if (match) {
            var action = match[1];
            xhr.addEventListener('load', function () {
                if (xhr.status >= 200 && xhr.status < 300) {
                    refreshCartAndDiff(action);
                }
            });
        }

        return _origXHRSend.apply(this, arguments);
    };

    // Keep theme DOM events as fallback (some themes do dispatch them)
    document.addEventListener('cart:change', function (e) {
        var d = e.detail || {}, c = d.cart; if (!c || !c.items) return;
        if (state.cart) { var diff = diffCart(state.cart, c); if (d.baseEvent !== 'variant:add' && diff.added.length) pushEvent('add_to_cart_stape', diff.added, c); if (diff.removed.length) pushEvent('remove_from_cart_stape', diff.removed, c); }
        state.cart = c; syncAttribution(c);
    });

    document.addEventListener('variant:add', function (e) {
        var d = e.detail || {}, items = d.items || []; if (!items.length) return;
        pushEvent('add_to_cart_stape', items.map(function (i) { return mapItem(i); }), d.cart || state.cart);
        syncAttribution(d.cart || state.cart);
    });

    document.addEventListener('click', function (event) {
        if (event && event.isTrusted === false) return;
        var t = event.target; if (!t || !t.closest) return;
        if (state.checkoutBypass) return;

        var ckBtn = t.closest(CHECKOUT_BUTTON_SELECTOR), dynT = t.closest(DYNAMIC_CHECKOUT_SELECTOR);
        var isDyn = !!dynT, ckLink = (!ckBtn && !dynT) ? findCheckoutLink(t) : null, href = null;
        if (ckLink) { href = ckLink.getAttribute('href') || null; if (href && !isCheckoutHref(href)) href = null; if (!href) return; }
        if (!ckBtn && !ckLink && !dynT) return;

        markIntent();
        if (isDyn) { handleDynIntent('dynamic_button_click'); return; }

        var form = ckBtn ? (ckBtn.form || ckBtn.closest('form')) : null;
        if (ckBtn && !form && !href) { if (state.cart && state.cart.items) pushBeginCheckout(state.cart, 'checkout_button_noform'); syncAttribution(state.cart || null, { keepalive: true }); return; }
        if (ckBtn && !form && !ckLink) return;

        var fp = JSON.stringify(buildAttrAttributes());
        if (state.attributionSynced !== fp) {
            if (state.checkoutGate) return;
            state.checkoutGate = true; event.preventDefault(); event.stopPropagation();

            var resume = function () {
                state.checkoutGate = false; state.checkoutBypass = true;
                try {
                    if (href) { window.location.assign(href); }
                    else if (form && typeof form.requestSubmit === 'function') { form.requestSubmit(ckBtn); }
                    else if (form) {
                        if (ckBtn && ckBtn.name) { var h = document.createElement('input'); h.type = 'hidden'; h.name = ckBtn.name; h.value = ckBtn.value || '1'; form.appendChild(h); form.submit(); form.removeChild(h); }
                        else form.submit();
                    }
                } catch (e) { state.checkoutBypass = false; }
                setTimeout(function () { state.checkoutBypass = false; }, 0);
            };

            var done = false, tmr = setTimeout(function () { if (done) return; done = true; resume(); }, CHECKOUT_GATE_TIMEOUT_MS);
            syncAttribution(state.cart || null, { keepalive: true })
                .then(function (c) { if (c) pushBeginCheckout(c, 'ck_click_sync'); else if (state.cart) pushBeginCheckout(state.cart, 'ck_click_state'); })
                .catch(function () { })
                .then(function () { if (done) return; done = true; clearTimeout(tmr); resume(); });
            return;
        }

        if (state.cart && state.cart.items) { pushBeginCheckout(state.cart, 'checkout_click'); return; }
        fetch(cartUrl(), { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (c) { state.cart = c; pushBeginCheckout(c, 'ck_click_fetch'); }).catch(function () { });
    }, true);

    document.addEventListener('submit', function (event) {
        if (event && event.isTrusted === false) return;
        var form = event.target; if (!form || !form.getAttribute || state.checkoutBypass) return;
        var action = getFormAction(form), sub = event.submitter || null;

        // Stretch-safe fallback: some product forms bypass cart events or use non-standard handlers.
        if (action && action.indexOf('/cart/add') !== -1) {
            pushAddToCartFromForm(form, 'form_submit');
            syncAttribution(state.cart || null, { keepalive: true });
            return;
        }

        var subIsCk = !!(sub && sub.matches && sub.matches(CHECKOUT_BUTTON_SELECTOR));
        var hasCk = !!form.querySelector(CHECKOUT_BUTTON_SELECTOR);
        var isDynForm = !!form.closest('.shopify-payment-button');
        var recent = (Date.now() - state.lastCheckoutIntentAt) <= CHECKOUT_INTENT_WINDOW_MS;
        if (!subIsCk && !((isCheckoutHref(action) || (isDynForm && hasCk)) && recent)) return;
        markIntent();
        if (state.cart && state.cart.items) pushBeginCheckout(state.cart, 'submit');
        syncAttribution(state.cart || null, { keepalive: true }).then(function (c) { if (c) pushBeginCheckout(c, 'submit_sync', { allowFallback: true }); }).catch(function () { });
    }, true);

    document.addEventListener('pointerdown', function (e) {
        if (e && e.isTrusted === false) return;
        if (!isCheckoutElement(e.target)) return;
        markIntent(); syncAttribution(state.cart || null, { keepalive: true, force: true });
    }, true);

    window.addEventListener('blur', function () {
        var a = document.activeElement; if (!isDynIframe(a)) return;
        markIntent();
        syncAttribution(state.cart || null, { keepalive: true, force: true });
    }, true);

    window.addEventListener('message', handleBridge);

    window.addEventListener('pagehide', function () { syncAttribution(state.cart || null, { keepalive: true }); });
    document.addEventListener('visibilitychange', function () { if (document.hidden) syncAttribution(state.cart || null, { keepalive: true }); });

})();
// copy2 in file

/*
copy3 edit note
- File edited: /Users/ahmedelmorshedy/Documents/Codexeditlandingpage copy3.js
- Exact changed lines: 568-572
- Edit made: expanded `chunkedKeys` inside `buildAttrAttributes()` so `landing_page`, `landing_page_first`, and `landing_page_last` are chunked like `fbc` and `fbclid`.
- Reason: long landing URLs were being truncated at the 255-char cart-attribute limit, which then shortened `event_source_url` even when `fbc` stayed complete.
*/

/*
2026-04-14 Codex hardening note
- File edited: /Users/ahmedelmorshedy/Downloads/theme_export__shawq-co-stretch__11APR2026-1013am/assets/gtm-events.js
- Exact changed lines: 37-41, 110-130, 501-545, 614-624, 797-823, 980-1025
- Edit made: added chunk cleanup, stricter fbclid/fbp/fbc validation, timeout-backed sync queue cleanup, and a safer cloning dataLayer wrapper while preserving the existing ShawQ checkout gate.
- Reason: keep the stronger Apr 11 checkout behavior, but surgically port the highest-value reliability improvements from the candidate script without changing ShawQ-specific checkout flow semantics.
*/

/*
Friday April 24, 2026 Codex purple button simplification note
- File edited: /Users/ahmedelmorshedy/Downloads/Codex Purple button setup/assets/gtm-events.js
- Business context: USA attribution was roughly 60-70% at the time, so attribution preservation was prioritized over aggressive add-to-cart/checkout count inflation.
- Exact changed lines after edit: 16-17, 51, 89, 772-795, 898-917, 1222-1225.
- Pre-edit code removed: `shopPayCartCreated` state, unused `cartAddUrl()`, old `pushAddToCartFromPage()` synthetic ATC helper, and old `ensureCartForShopPay()` cart-creation flow.
- Old risky behavior removed: purple/dynamic checkout intent no longer pushes synthetic `add_to_cart_stape`, no longer POSTs to `/cart/add.js`, and no longer hardcodes quantity `1` to create a cart before Shop Pay.
- New dynamic checkout behavior: purple/dynamic checkout still forces attribution sync with `keepalive: true` and `force: true`; if cart data exists it emits `begin_checkout_stape` from the cart, and if cart data is absent on a product page it emits `begin_checkout_stape` from product/variant data without mutating the cart.
- Iframe blur change: Shop Pay iframe blur now only marks checkout intent and syncs attribution; it no longer calls the full dynamic checkout handler or emits AddToCart/BeginCheckout by itself.
- Purpose preserved: keep the purple buttons, protect `fbc`, `fbp`, landing page, external_id, currency, and user data before fast checkout handoff, while making AddToCart represent real cart/form/cart API behavior only.
*/

/*
Friday May 1, 2026 Codex attribution URL + dynamic checkout ATC note
- File edited: live Shopify theme `Stretch` (#176802070826), asset `assets/gtm-events.js`.
- Rollback source saved before editing: `/Users/ahmedelmorshedy/.codex/skills/ShawqGTMJsBundle/06-current-live-source-before-2026-05-01-gtm-events.js`.
- Exact behavior changed: top-level `landing_page` and `event_source_url` on Stape events now prefer the current browser URL, while older stored landing URLs remain inside `attribution.landing_page_first` / `attribution.landing_page_last` for history.
- Exact behavior changed: current URL parameters now get copied to the event payload when present: `utm_source`, `utm_medium`, `utm_campaign`, `utm_content`, `utm_term`, `utm_id`, `campaign_id`, `adset_id`, `ad_id`, `placement`, and `fbclid`.
- Exact behavior changed: `getAttribution()` still persists first/last touch values, but the root `attribution.landing_page` is now the current page instead of an old stored touch. This prevents old campaigns from masquerading as the active event URL.
- Exact behavior changed: dynamic/purple checkout on product pages now emits a tracking-only `add_to_cart_stape` from the selected product before `begin_checkout_stape`, with `add_to_cart_mode: dynamic_checkout_tracking_only`.
- What was intentionally not brought back: no `cart/add.js` POST, no synthetic cart creation, no hardcoded quantity-1 cart mutation, and no Shop Pay iframe blur checkout event emission.
- Why: protect correct Meta attribution and current landing URLs first, while reducing the ATC undercount from Buy Now / purple dynamic checkout routes that bypass the normal cart API.
*/

/* ============================================================================
 * CHANGE LOG — v08 vs v07   (Friday, June 19, 2026 — Codex)
 * File: 08-best-merge-identity-fbc-continuity-2026-06-19-gtm-events.js
 * Prev: 07-after-attribution-url-atc-fix-2026-05-01-gtm-events.js
 * ----------------------------------------------------------------------------
 * WHY: a Meta-paid visitor who later returns via "link in bio" (an unpaid
 * last-touch URL) was minting a fresh guest external_id and losing the original
 * fbc, so the eventual purchase no longer credited the originating ad in CAPI.
 * v08 adds an identity + fbc continuity spine so the same person keeps the same
 * external_id and original fbc across visits and across Safari ITP eviction.
 *
 * CHANGES:
 * 1. IndexedDB identity recovery (snapshotIdentity / persistIdentityToIDB /
 *    rehydrateIdentityFromIDB). A dedicated 'identity' IDB record now carries
 *    external_id + sticky fbc/fbclid + first-touch paid context (campaign_id,
 *    adset_id, ad_id, utm_source/campaign/content_first, landing_page_first).
 *    On boot it is recovered into cookies/LS ONLY for fields currently missing
 *    (ITP eviction) — a genuine existing id is never clobbered.
 * 2. Provisional-id self-heal. getExternalId() runs synchronously (page_view)
 *    before async IDB resolves, so a boot-minted id is tagged state.provisionalEid
 *    and the IDB-recovered id is allowed to replace ONLY that provisional id.
 * 3. Boot ordering (bootWithIdentityRecovery): identity is rehydrated from IDB
 *    BEFORE the first attribution sync, with an 800ms timeout fallback so init
 *    never blocks on IndexedDB; a late recovery forces one re-sync.
 * 4. applyPaidAttributionContext(): on every Stape event, when the current URL
 *    carries no paid identifiers, first-touch fbc/fbclid/campaign_id/adset_id/
 *    ad_id are promoted to top level (current-URL values always win; first-touch
 *    only fills gaps), with paid_click + attributed_channel='meta_paid' flags.
 * 5. fbc->ad visibility: a single inspectable 'meta_ad_attribution' blob is added
 *    to paid events (fbc/fbclid + campaign/adset/ad ids + campaign_name/ad_name
 *    from utm_campaign/utm_content + source) so the ad behind an fbc is readable
 *    in the dataLayer / Stape request / Meta Events Manager.
 * 6. Accelerated checkout (Shop Pay / Buy Now): handleDynIntent() snapshots the
 *    identity spine to IDB the instant intent fires, in addition to the existing
 *    keepalive cart-attribute sync, so attribution survives the jump even when
 *    the buttons bypass the normal cart flow.
 *
 * SCOPE: client-side enrichment + persistence only. No change to event names,
 * dedup/event_id, server GTM, or the Stape endpoint. external_id == user_id by
 * design (stable id reused for both); unchanged from v07.
 * ============================================================================ */
